import { useState } from "react";
import { Mail, Check, Loader2, Send, Copy } from "lucide-react";
import api from "@/lib/api";

/**
 * Bloc "Accès client" — version compacte Sprint V1.
 *
 * Objectif : tenir sur une seule carte, sans informations techniques.
 * Affiche :
 *   - le statut (Actif / Invitation envoyée / Expiré / Pas d'invitation)
 *   - l'email
 *   - Renvoyer l'invitation (si email + statut pending)
 *   - Copier le lien client (magic link)
 *
 * Retirés du sprint :
 *   - Date d'invitation
 *   - Première connexion
 *   - Dernière connexion
 *   - Réinitialisation du mot de passe
 */

const STATUS_MAP = {
  not_invited: { dot: "bg-[#D1D5DB]", label: "Pas d'invitation" },
  pending:     { dot: "bg-[#F59E0B]", label: "Invitation envoyée" },
  activated:   { dot: "bg-[#C8FF2F]", label: "Actif" },
  password_set:{ dot: "bg-[#22C55E]", label: "Actif" },
  expired:     { dot: "bg-[#EF4444]", label: "Expiré" },
};

const ActionButton = ({ icon: Icon, label, onClick, busy, done, testId }) => (
  <button
    onClick={onClick}
    disabled={busy}
    data-testid={testId}
    className="h-9 inline-flex items-center gap-2 rounded-full px-3.5 text-[12.5px] font-medium
               bg-white border border-[#EEF2F5] text-[#111111] hover:bg-[#F7F8FA]
               transition-colors disabled:opacity-60"
  >
    {busy ? (
      <Loader2 size={13} className="animate-spin" strokeWidth={2.25} />
    ) : done ? (
      <Check size={13} strokeWidth={2.5} />
    ) : (
      <Icon size={13} strokeWidth={2} />
    )}
    {done ? "Envoyé" : label}
  </button>
);

const ClientAccountCard = ({ dossierId, clientAccount, clientEmail }) => {
  const [busy, setBusy] = useState({ resend: false, magic: false });
  const [done, setDone] = useState({ resend: false, magic: false });
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);

  const ca = clientAccount || { status: "not_invited" };
  const status = STATUS_MAP[ca.status] || STATUS_MAP.not_invited;
  const hasEmail = Boolean(clientEmail);

  const runAction = async (key, fn) => {
    setBusy((b) => ({ ...b, [key]: true }));
    setError(null);
    try {
      const res = await fn();
      setDone((d) => ({ ...d, [key]: true }));
      setTimeout(() => setDone((d) => ({ ...d, [key]: false })), 2200);
      return res;
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setBusy((b) => ({ ...b, [key]: false }));
    }
  };

  const onResend = () => runAction("resend", () => api.resendClientInvitation(dossierId));

  const onCopyMagic = async () => {
    setBusy((b) => ({ ...b, magic: true }));
    setError(null);
    try {
      const res = await api.generateClientMagicLink(dossierId);
      if (res?.url) {
        try {
          await navigator.clipboard.writeText(res.url);
          setCopied(true);
          setTimeout(() => setCopied(false), 1800);
        } catch {
          setError("Impossible de copier — lien : " + res.url);
        }
      }
    } catch (e) {
      setError(e.message || "Erreur");
    } finally {
      setBusy((b) => ({ ...b, magic: false }));
    }
  };

  return (
    <div
      data-testid="client-account-card"
      className="rounded-3xl bg-white border border-[#EEF2F5] px-5 py-4"
    >
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              className={`h-2 w-2 rounded-full ${status.dot}`}
              aria-hidden
              data-testid="client-status-dot"
            />
            <p
              data-testid="client-status-label"
              className="text-[13px] font-semibold text-[#111111] tracking-tight"
            >
              Accès client · {status.label}
            </p>
          </div>
          <p className="mt-1 text-[12.5px] text-[#6B7280] truncate">
            {hasEmail ? clientEmail : "Aucun email renseigné sur le dossier"}
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {hasEmail && ca.status !== "activated" && ca.status !== "password_set" && (
            <ActionButton
              icon={Send}
              label="Renvoyer l'invitation"
              busy={busy.resend}
              done={done.resend}
              onClick={onResend}
              testId="btn-resend-invitation"
            />
          )}
          {hasEmail && (
            <button
              onClick={onCopyMagic}
              disabled={busy.magic}
              data-testid="btn-copy-client-link"
              className="h-9 inline-flex items-center gap-2 rounded-full px-3.5 text-[12.5px] font-medium
                         bg-[#111111] text-white hover:bg-black transition-colors disabled:opacity-60"
            >
              {busy.magic ? (
                <Loader2 size={13} className="animate-spin" strokeWidth={2.25} />
              ) : copied ? (
                <>
                  <Check size={13} strokeWidth={2.5} />
                  Lien copié
                </>
              ) : (
                <>
                  <Copy size={13} strokeWidth={2} />
                  Copier le lien client
                </>
              )}
            </button>
          )}
        </div>
      </div>

      {error && (
        <p data-testid="client-card-error" className="mt-2 text-[12px] text-[#B91C1C]">
          {error}
        </p>
      )}
    </div>
  );
};

export default ClientAccountCard;
